import React from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Clock, Pencil } from "lucide-react";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";

export const APPOINTMENT_TYPES_PATH =
  "/dashboard/inbound-calls/appointment-types";

interface AppointmentTypeSummary {
  name: string;
  durationMinutes: number;
}

const DEMO_TYPES: AppointmentTypeSummary[] = [
  { name: "New Patient Consultation", durationMinutes: 30 },
  { name: "Mental Health Session", durationMinutes: 60 },
  { name: "Follow-Up Visit", durationMinutes: 15 },
];

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  if (remaining === 0) return `${hours} hr`;
  return `${hours} hr ${remaining} min`;
}

function AppointmentTypesCard() {
  return (
    <Card className="gap-3 py-4">
      <CardHeader className="px-4">
        <CardTitle className="flex items-center gap-2 text-sm">
          <CalendarClock className="h-4 w-4 text-gray-500" />
          Appointment types
        </CardTitle>
        <CardDescription className="text-xs">
          {DEMO_TYPES.length} types defined. The assistant uses these to find
          the right time slot length when a caller asks to schedule.
        </CardDescription>
      </CardHeader>
      <CardContent className="px-4 space-y-2">
        {DEMO_TYPES.map((type) => (
          <div
            key={type.name}
            className="flex items-center justify-between text-xs"
          >
            <span className="text-gray-700">{type.name}</span>
            <span className="inline-flex items-center gap-1 text-gray-500">
              <Clock className="h-3 w-3" />
              {formatDuration(type.durationMinutes)}
            </span>
          </div>
        ))}
        <div className="pt-1">
          <Link
            to={APPOINTMENT_TYPES_PATH}
            data-testid="manage-appointment-types"
          >
            <Button
              size="sm"
              className="h-8 text-xs bg-brand hover:bg-brand-hover text-white"
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5 shrink-0" />
              Manage appointment types
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default AppointmentTypesCard;
